import { type Market, MarketStatus } from '@/libs/sdk/api';

import { getBinaryMarketOptions, getBinaryProbabilityPercentage } from '../market-card/binary-options';

export type EventCardRow = {
  market: Market;
  probability: number;
  yes: Market['options'][number];
  no: Market['options'][number];
};

export type EventCardModel = {
  title: string;
  leadMarket: Market;
  status: MarketStatus;
  totalMarkets: number;
  totalParticipants: number;
  totalVolume: number;
  nearestDeadline?: number;
  displayRows: EventCardRow[];
  remainingCount: number;
};

// Rows are sorted by probability, so the visible ones are always the legs that
// carry the most information. Three made grouped-event cards tall enough to
// crowd the grid; two plus a "+N more" keeps every card the same height.
const MAX_DISPLAY_ROWS = 2;

// The distinctive middle of each sibling leg name: what is left after removing
// the question stem and tail that every leg in the event shares. Across the four
// Brent legs "Will Brent crude settle {below $65 | between $65 and $75 | ...} at
// the end of August?" this returns just "below $65", "between $65 and $75", etc.
// The Fed legs reduce to "cut" / "hold" / "raise", the UCL legs to "Real Madrid".
//
// Shared affixes are snapped to a word boundary so a run like "...$65 ..." /
// "...$75 ..." cannot leave a dangling "5". With fewer than two siblings, or when
// stripping would empty a label, the whole trimmed name is returned instead.
const AFFIX_TRIM = /^[\s:?·\-—,.]+|[\s:?·\-—,.]+$/g;

export const distinctiveLabels = (names: string[]): string[] => {
  const trimmed = names.map(n => (n || '').trim());
  if (trimmed.length < 2) return trimmed.map(s => s.replace(AFFIX_TRIM, ''));

  const shortest = trimmed.reduce((a, b) => (b.length < a.length ? b : a));
  let pre = 0;
  while (pre < shortest.length && trimmed.every(s => s[pre] === shortest[pre])) pre++;
  pre = shortest.lastIndexOf(' ', pre) + 1; // snap back to a word boundary

  let suf = 0;
  while (suf < shortest.length - pre &&
         trimmed.every(s => s[s.length - 1 - suf] === shortest[shortest.length - 1 - suf])) suf++;
  const suffixText = shortest.slice(shortest.length - suf);
  const spaceInSuffix = suffixText.indexOf(' ');
  suf = spaceInSuffix >= 0 ? suf - spaceInSuffix : 0; // start the shared tail at a word boundary

  return trimmed.map(s => {
    const mid = s.slice(pre, s.length - suf).replace(AFFIX_TRIM, '');
    return mid || s.replace(AFFIX_TRIM, '');
  });
};

export const resolveEventCardHref = (markets: Market[], eventId: string) => {
  if (markets.length === 1 && markets[0]?.marketAddress) {
    return `/market/${markets[0].marketAddress}`;
  }

  return `/event/${eventId}`;
};

export const buildEventCardModel = (markets: Market[], title: string): EventCardModel | null => {
  const leadMarket = markets[0];
  const rows = markets
    .map(market => {
      const { yes, no } = getBinaryMarketOptions(market.options);
      if (!yes || !no) return null;

      return {
        market,
        yes,
        no,
        probability: getBinaryProbabilityPercentage(market.options) ?? 50,
      };
    })
    .filter((row): row is EventCardRow => row !== null)
    .sort((a, b) => b.probability - a.probability);

  if (!leadMarket) return null;

  // Event-level status = the LEAST-advanced sub-market. A grouped "which team
  // wins" event is only final when EVERY sub-market has closed; a single
  // eliminated outcome settling (one team out) must not flip the whole group to
  // "final result". progress(1) < settlement(2) < disputed(3) < closed(4).
  const status = markets.reduce<MarketStatus>((min, market) => {
    const s = market.marketStatus ?? MarketStatus.progress;
    return s < min ? s : min;
  }, MarketStatus.closed);

  const totalParticipants = markets.reduce((sum, market) => sum + (market.participantsCount || 0), 0);
  const totalVolume = markets.reduce((sum, market) => sum + Number(market.marketVolume || 0), 0);
  const nearestDeadline = markets.reduce<number | undefined>((min, market) => {
    const deadline = Number(market.deadline);
    if (!deadline) return min;
    return min === undefined ? deadline : Math.min(min, deadline);
  }, undefined);

  return {
    title,
    leadMarket,
    status,
    totalMarkets: markets.length,
    totalParticipants,
    totalVolume,
    nearestDeadline,
    displayRows: rows.slice(0, MAX_DISPLAY_ROWS),
    remainingCount: Math.max(0, rows.length - MAX_DISPLAY_ROWS),
  };
};
